import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  TextField,
  Grid,
} from "@mui/material";
import { translateText } from "../translations/translationService";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { traducirMetal } from "../services/Functions";
import { useSelector, useDispatch } from "react-redux";
import {
  getListMetals,
  getListMoWithAleacion,
  createNewGrouping,
  deleteMOFromFoundry,
  getListFullGroup,
  deleteElementFromGroup,
  UpdatefinishAgrupacion,
  insertNewMetalToGroup,
  resetMOFoundry,
} from "../services/ConnectDataBase";
import { getCurrentDateTime, formatDate } from "../services/Functions";
import { SCREEN_IDS } from "../components/FullComponent.js";
import {
  setListMetals,
  setGroupList,
  setListAleacionOption,
} from "../redux/reducers";
import { typeConnect, backgroundColor2 } from "../constants/Constants";
import { FOUNDRY_SCREEN_COLORS } from "../style/colors.js";

const FoundryScreen = () => {
  const dispatch = useDispatch();
  const { name } = useSelector((state) => state.user);
  const listMetals = useSelector((state) => state.listMetals);
  const groupList = useSelector((state) => state.groupList);
  const listAleacionOption = useSelector((state) => state.listAleacionOption);
  const { value: lang } = useSelector((state) => state.lang);

  // Estados agrupados por función
  const [ui, setUi] = useState({
    selectedMetal: null,
    selectedAgrupaciones: [],
    expandedGroups: [],
    showModal: false,
    isResetModalOpen: false,
    selectAll: false,
  });

  const [search, setSearch] = useState({
    refSearch: "",
    sortOrder: "asc",
    originalListAleacionOption: [],
    searchCodes: [],
    pendingCodes: [], // Códigos escaneados pendientes de búsqueda
    lastInputLength: 0,
  });

  const [form, setForm] = useState({
    moReset: "",
    selectedAgrupacion: null,
    newAddOption: null,
    inputValues: {},
  });

  const currentScreenId = SCREEN_IDS.FOUNDRY;

  // Memoización del cálculo de gramos totales
  const sumTotalGramos = useMemo(() => {
    const total = ui.selectedAgrupaciones.reduce(
      (sum, agrupacion) =>
        sum + parseFloat(agrupacion.gramos) * agrupacion.cantidad,
      0
    );
    return parseFloat(total.toFixed(2));
  }, [ui.selectedAgrupaciones]);

  // Memoización del estilo de botón
  const buttonStyle = useMemo(
    () => ({
      backgroundColor: backgroundColor2,
      color: "white",
      "&:hover": {
        backgroundColor: FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_HOVER,
      },
    }),
    []
  );

  // Función para normalizar códigos (cambiar ' por -)
  const normalizeCode = useCallback((code) => {
    return code.replace(/'/g, "-");
  }, []);

  // Función para separar códigos cada 10 caracteres
  const handleInputChange = useCallback(
    (value) => {
      // Normalizar caracteres primero
      const normalized = normalizeCode(value);

      // Eliminar espacios existentes para reprocessar
      const withoutSpaces = normalized.replace(/\s/g, "");

      // Agregar espacio cada 10 caracteres
      const formatted = withoutSpaces.replace(/(.{10})/g, "$1 ").trim();

      // Procesar códigos válidos y agregarlos a pendingCodes
      const newCodes = processCodes(formatted);
      const validCodes = newCodes.filter(code => 
        code.length === 10 && code.includes("-")
      );

      setSearch((prev) => ({
        ...prev,
        refSearch: formatted,
        lastInputLength: formatted.length,
        pendingCodes: [...new Set([...prev.pendingCodes, ...validCodes])], // Evitar duplicados
      }));
    },
    [normalizeCode, processCodes]
  );
  const processCodes = useCallback(
    (input) => {
      if (!input.trim()) return [];

      // Normalizar el input completo
      const normalizedInput = normalizeCode(input);

      // Dividir por espacios y filtrar códigos válidos (formato xxxxxx-xxx)
      const codes = normalizedInput
        .split(/\s+/)
        .filter((code) => code.length === 10 && code.includes("-"))
        .map((code) => code.toUpperCase().trim());

      return [...new Set(codes)]; // Eliminar duplicados
    },
    [normalizeCode]
  );
  // Funciones de API optimizadas
  const apiCalls = useMemo(
    () => ({
      getMetalsFoundry: async () => {
        try {
          const options = await getListMetals(currentScreenId, typeConnect[0]);
          dispatch(setListMetals(options));
        } catch (error) {
          console.error(error);
        }
      },

      getGroupListFoundry: async () => {
        try {
          const options = await getListFullGroup(
            currentScreenId,
            typeConnect[0]
          );
          dispatch(setGroupList(options));
        } catch (error) {
          console.error(error);
        }
      },

      getMoWithAleacion: async (aleacion, sortBy) => {
        try {
          const listFullMo = await getListMoWithAleacion(
            currentScreenId,
            typeConnect[0],
            aleacion
          );

          if (!listFullMo) {
            dispatch(setListAleacionOption([]));
            return;
          }

          const sortedList = [...listFullMo].sort((a, b) => {
            switch (sortBy) {
              case "gramos":
                return search.sortOrder === "asc"
                  ? a.gramos - b.gramos
                  : b.gramos - a.gramos;
              case "cantidad":
                return b.cantidad - a.cantidad;
              case "fecha":
              default:
                return new Date(a.fechaentrega) - new Date(b.fechaentrega);
            }
          });

          dispatch(setListAleacionOption(sortedList));
          // Actualizar la lista original para búsquedas
          setSearch((prev) => ({
            ...prev,
            originalListAleacionOption: sortedList,
          }));
        } catch (info) {
          console.info("No hay MO disponibles");
        }
      },
    }),
    [currentScreenId, dispatch, search.sortOrder]
  );

  // Effects optimizados
  useEffect(() => {
    Promise.all([apiCalls.getMetalsFoundry(), apiCalls.getGroupListFoundry()]);
  }, []);

  useEffect(() => {
    if (ui.selectedMetal) {
      apiCalls.getMoWithAleacion(ui.selectedMetal.aleacion);
    }
  }, [ui.selectedMetal, apiCalls]);

  useEffect(() => {
    apiCalls.getGroupListFoundry();
  }, [ui.selectedAgrupaciones, apiCalls]);

  // Handlers optimizados
  const handlers = useMemo(
    () => ({
      metalSelect: (metal) => {
        setUi((prev) => ({
          ...prev,
          selectedMetal:
            prev.selectedMetal?.aleacion === metal.aleacion ? null : metal,
          selectedAgrupaciones: [],
        }));
      },

      agrupacionSelect: (agrupacion) => {
        setUi((prev) => ({
          ...prev,
          selectedAgrupaciones: prev.selectedAgrupaciones.some(
            (item) => item.id === agrupacion.id
          )
            ? prev.selectedAgrupaciones.filter(
                (item) => item.id !== agrupacion.id
              )
            : [...prev.selectedAgrupaciones, agrupacion],
        }));
      },

      groupToggle: (agrupacionId) => {
        setUi((prev) => ({
          ...prev,
          expandedGroups: prev.expandedGroups.includes(agrupacionId)
            ? prev.expandedGroups.filter((id) => id !== agrupacionId)
            : [...prev.expandedGroups, agrupacionId],
        }));
      },

      search: () => {
        // Usar los códigos pendientes en lugar de procesar el input actual
        const searchCodes = search.pendingCodes;

        if (searchCodes.length === 0) {
          // Si no hay códigos válidos, mostrar toda la lista original
          dispatch(setListAleacionOption(search.originalListAleacionOption));
          setSearch((prev) => ({ ...prev, searchCodes: [], pendingCodes: [] }));
          return;
        }

        // Filtrar por los códigos encontrados
        const filteredResults = search.originalListAleacionOption.filter(
          (item) =>
            searchCodes.some((code) => item.mo.toUpperCase().includes(code))
        );

        dispatch(setListAleacionOption(filteredResults));
        setSearch((prev) => ({ 
          ...prev, 
          searchCodes, 
          refSearch: "", 
          pendingCodes: [] // Limpiar códigos pendientes después de buscar
        }));
      },

      clearSearch: () => {
        // Limpiar búsqueda y mostrar lista completa
        dispatch(setListAleacionOption(search.originalListAleacionOption));
        setSearch((prev) => ({ 
          ...prev, 
          refSearch: "", 
          searchCodes: [], 
          pendingCodes: [] // También limpiar códigos pendientes
        }));
      },

      selectAll: () => {
        if (ui.selectAll) {
          setUi((prev) => ({
            ...prev,
            selectedAgrupaciones: [],
            selectAll: false,
          }));
        } else {
          setUi((prev) => ({
            ...prev,
            selectedAgrupaciones: [...listAleacionOption],
            selectAll: true,
          }));
        }
      },

      consultaAsync: (item) => {
        setForm((prev) => ({ ...prev, newAddOption: item }));
        setUi((prev) => ({ ...prev, showModal: true }));
      },

      toggleResetModal: () => {
        setUi((prev) => ({
          ...prev,
          isResetModalOpen: !prev.isResetModalOpen,
        }));
      },
    }),
    [
      ui.selectedMetal,
      ui.selectedAgrupaciones,
      ui.expandedGroups,
      ui.selectAll,
      search,
      listAleacionOption,
      dispatch,
      processCodes,
      handleInputChange,
    ]
  );

  // Funciones de acciones asíncronas
  const actions = useMemo(
    () => ({
      crearAgrupacion: async () => {
        try {
          await createNewGrouping(
            name,
            currentScreenId,
            ui.selectedMetal,
            ui.selectedAgrupaciones,
            typeConnect[1],
            getCurrentDateTime()
          );

          await Promise.all([
            apiCalls.getMetalsFoundry(),
            apiCalls.getGroupListFoundry(),
          ]);

          dispatch(setListAleacionOption([]));
          setUi((prev) => ({ ...prev, selectedAgrupaciones: [] }));

          if (ui.selectedAgrupaciones[0]) {
            apiCalls.getMoWithAleacion(ui.selectedAgrupaciones[0].aleacion);
          }
        } catch (error) {
          console.error(error);
        }
      },

      borrarMO: async (item) => {
        try {
          await deleteMOFromFoundry(
            currentScreenId,
            typeConnect[3],
            item.mo,
            item.aleacion
          );
          setUi((prev) => ({ ...prev, selectedAgrupaciones: [] }));

          await Promise.all([
            apiCalls.getMetalsFoundry(),
            apiCalls.getGroupListFoundry(),
            apiCalls.getMoWithAleacion(item.aleacion),
          ]);
        } catch (error) {
          console.error(error);
        }
      },

      borrarAgrupacion: async (agrupacion) => {
        try {
          await deleteElementFromGroup(
            currentScreenId,
            typeConnect[3],
            agrupacion.mo,
            agrupacion.aleacion
          );
          setUi((prev) => ({ ...prev, selectedAgrupaciones: [] }));

          await Promise.all([
            apiCalls.getMetalsFoundry(),
            apiCalls.getGroupListFoundry(),
            apiCalls.getMoWithAleacion(agrupacion.aleacion),
          ]);
        } catch (error) {
          console.error(error);
        }
      },

      finishAgrupacion: async (agrupacion, inputValue) => {
        try {
          await UpdatefinishAgrupacion(
            currentScreenId,
            typeConnect[2],
            agrupacion,
            inputValue
          );
          setUi((prev) => ({ ...prev, selectedAgrupaciones: [] }));
          setForm((prev) => ({
            ...prev,
            inputValues: { ...prev.inputValues, [agrupacion]: "" },
          }));

          await Promise.all([
            apiCalls.getMetalsFoundry(),
            apiCalls.getGroupListFoundry(),
            apiCalls.getMoWithAleacion(ui.selectedMetal?.aleacion),
          ]);
        } catch (error) {
          console.error(error);
        }
      },

      addToGroup: async () => {
        try {
          const agrupacionItems = groupList[form.selectedAgrupacion];
          const newAddWithFecha = {
            ...form.newAddOption,
            fecha_agrupacion: agrupacionItems[0].fecha_agrupacion,
          };

          await insertNewMetalToGroup(
            currentScreenId,
            typeConnect[1],
            form.selectedAgrupacion,
            newAddWithFecha
          );

          await Promise.all([
            apiCalls.getMetalsFoundry(),
            apiCalls.getGroupListFoundry(),
          ]);

          dispatch(setListAleacionOption([]));
          setUi((prev) => ({
            ...prev,
            selectedAgrupaciones: [],
            showModal: false,
          }));
          setForm((prev) => ({ ...prev, selectedAgrupacion: null }));

          apiCalls.getMoWithAleacion(form.newAddOption.aleacion);
        } catch (error) {
          console.error(error);
        }
      },

      resetMo: async () => {
        try {
          await resetMOFoundry(currentScreenId, typeConnect[2], form.moReset);
          setForm((prev) => ({ ...prev, moReset: "" }));
        } catch (error) {
          console.error(error);
        }
      },
    }),
    [ui, form, groupList, name, currentScreenId, apiCalls, dispatch]
  );

  // Componentes memoizados
  const ResetModal = useMemo(
    () => (
      <Modal open={ui.isResetModalOpen} onClose={handlers.toggleResetModal}>
        <Box
          sx={{
            backgroundColor: "white",
            padding: 1.5,
            margin: "auto",
            marginTop: "10%",
            maxWidth: 400,
            borderRadius: 2,
          }}>
          <Typography variant='h6' sx={{ fontSize: "0.95rem", fontWeight: 600, mb: 1.5 }}>
            {translateText("Reset a Fundición", lang)}
          </Typography>
          <TextField
            size='small'
            variant='outlined'
            value={form.moReset}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, moReset: e.target.value }))
            }
            placeholder='MO'
            fullWidth
            sx={{ 
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-input": {
                  py: 0.75,
                },
              },
            }}
          />
          <Button
            variant='contained'
            size="small"
            sx={[buttonStyle, {
              fontSize: "0.85rem",
              py: 0.75,
            }]}
            onClick={() => {
              actions.resetMo();
              handlers.toggleResetModal();
            }}
            fullWidth>
            {translateText("Reset a Fundición", lang)}
          </Button>
        </Box>
      </Modal>
    ),
    [
      ui.isResetModalOpen,
      form.moReset,
      handlers.toggleResetModal,
      actions.resetMo,
      buttonStyle,
      lang,
    ]
  );

  const ConsultaModal = useMemo(
    () => (
      <Modal
        open={ui.showModal}
        onClose={() => setUi((prev) => ({ ...prev, showModal: false }))}>
        <Box
          sx={{
            backgroundColor: "white",
            padding: 1.5,
            margin: "auto",
            marginTop: "10%",
            maxWidth: 400,
            borderRadius: 2,
          }}>
          <Typography variant='h6' sx={{ fontSize: "0.95rem", fontWeight: 600, mb: 1.5 }}>
            {translateText("Resultados de la consulta", lang)}
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: "auto", mt: 1.5 }}>
            {Object.keys(groupList)
              .filter((agrupacion) =>
                agrupacion.includes(ui.selectedMetal?.aleacion)
              )
              .map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: 0.75,
                    cursor: "pointer",
                    backgroundColor:
                      form.selectedAgrupacion === item ? FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_HOVER : "white",
                    "&:hover": { backgroundColor: FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_HOVER },
                  }}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, selectedAgrupacion: item }))
                  }>
                  <Typography sx={{ fontSize: "0.85rem" }}>{item}</Typography>
                </Box>
              ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 1.5 }}>
            <Button
              variant='contained'
              size="small"
              onClick={actions.addToGroup}
              sx={[buttonStyle, {
                fontSize: "0.85rem",
                py: 0.75,
                flex: 1,
              }]}>
              {translateText("Aceptar", lang)}
            </Button>
            <Button
              variant='outlined'
              size="small"
              onClick={() => setUi((prev) => ({ ...prev, showModal: false }))}
              sx={[buttonStyle, {
                fontSize: "0.85rem",
                py: 0.75,
                flex: 1,
              }]}>
              {translateText("Cancelar", lang)}
            </Button>
          </Box>
        </Box>
      </Modal>
    ),
    [
      ui.showModal,
      ui.selectedMetal,
      form.selectedAgrupacion,
      groupList,
      actions.addToGroup,
      buttonStyle,
      lang,
    ]
  );

  return (
    <Box sx={{ padding: 1.5 }}>
      {ResetModal}
      {ConsultaModal}

      <Grid container spacing={1.5}>
        {/* Tabla de Metales */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ padding: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant='h6' sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
                {translateText("Tabla de Metales", lang)}
              </Typography>
              <Typography variant='subtitle1' sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                {translateText("Total:", lang)} {sumTotalGramos} gr
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: "34vh", minHeight: "34vh" }}>
              <Table size='small' stickyHeader>
                <TableBody>
                  {listMetals.map((item, index) => (
                    <TableRow
                      key={item.aleacion}
                      selected={ui.selectedMetal?.aleacion === item.aleacion}
                      onClick={() => handlers.metalSelect(item)}
                      hover
                      sx={{
                        backgroundColor: index % 2 === 0 ? FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_EVEN : FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_ODD,
                        "&.Mui-selected": { backgroundColor: FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_SELECTED },
                        "&:hover": { backgroundColor: `${FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_HOVER} !important` },
                      }}>
                      <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>
                        {traducirMetal(item.descripcion, lang)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Tabla de Agrupaciones */}
        <Grid item xs={12} md={9}>
          <Paper
            sx={{
              padding: 1.5,
              minHeight: "38vh",
              maxHeight: "38vh",
              overflowY: "auto",
            }}>
            <Typography variant='h6' sx={{ fontSize: "0.95rem", fontWeight: 600, mb: 1 }}>
              {translateText("Tabla de Agrupaciones", lang)}
            </Typography>
            {groupList &&
              Object.keys(groupList).map((agrupacion) => (
                <Box key={agrupacion} sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handlers.groupToggle(agrupacion)}
                      sx={{ p: 0.5 }}>
                      {ui.expandedGroups.includes(agrupacion) ? (
                        <ExpandLess sx={{ fontSize: 20 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                    <Typography variant='subtitle1' sx={{ flex: 1, fontSize: "0.85rem", fontWeight: 500 }}>
                      {agrupacion} - {translateText("Nº líneas:", lang)}{" "}
                      {groupList[agrupacion].length}
                    </Typography>
                    <TextField
                      size='small'
                      variant='outlined'
                      placeholder='Número Lote'
                      value={form.inputValues[agrupacion] || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          inputValues: {
                            ...prev.inputValues,
                            [agrupacion]: e.target.value,
                          },
                        }))
                      }
                      sx={{
                        width: 140,
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.75rem",
                          height: "28px",
                          "& .MuiOutlinedInput-input": {
                            py: 0.5,
                          },
                        },
                      }}
                    />
                    <Button
                      variant='contained'
                      size="small"
                      onClick={() =>
                        actions.finishAgrupacion(
                          agrupacion,
                          form.inputValues[agrupacion]
                        )
                      }
                      sx={[buttonStyle, { 
                        ml: 0.5,
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                        minWidth: "auto",
                      }]}>
                      {translateText("Finalizar", lang)}
                    </Button>
                  </Box>
                  {ui.expandedGroups.includes(agrupacion) && (
                    <TableContainer
                      component={Paper}
                      sx={{
                        mt: 1,
                        maxHeight: "34vh",
                        overflowY: "auto",
                        backgroundColor: FOUNDRY_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                      }}>
                      <Table size='small'>
                        <TableHead>
                          <TableRow>
                            {[
                              "#",
                              "MO",
                              "Aleación",
                              "Referencia",
                              "Fecha Agrupación",
                              "Cantidad",
                              "Gramos",
                              "Acciones",
                            ].map((header) => (
                              <TableCell 
                                key={header}
                                sx={{ 
                                  py: 1, 
                                  px: 1.5, 
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                }}>
                                {translateText(header, lang)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupList[agrupacion].map((objeto, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor:
                                  index % 2 === 0 ? FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_EVEN : FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_ODD,
                              }}>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{index + 1}</TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{objeto.mo}</TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{objeto.aleacion}</TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{objeto.referencia}</TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>
                                {objeto.fecha_agrupacion.date.split(" ")[0]}
                              </TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>
                                {parseFloat(objeto.cantidad).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{objeto.gramos} gr</TableCell>
                              <TableCell sx={{ py: 0.75, px: 1.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    actions.borrarAgrupacion(objeto)
                                  }
                                  color='error'
                                  sx={{ p: 0.5 }}>
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ))}
          </Paper>
        </Grid>

        {/* Tabla de MO */}
        <Grid item xs={12}>
          <Paper sx={{ padding: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: 1,
                borderRadius: 1,
                gap: 1,
              }}>
              <InputBase
                value={search.refSearch}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder='Escanear códigos'
                sx={{
                  flex: 1,
                  backgroundColor: "transparent",
                  color: FOUNDRY_SCREEN_COLORS.TEXT_BRAND,
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  padding: "6px 12px",
                  borderRadius: 2,
                  border: `1.5px solid ${FOUNDRY_SCREEN_COLORS.BORDER_BRAND}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 2px 8px rgba(70, 93, 100, 0.2)",
                  },
                  "& input": {
                    padding: 0,
                  },
                  "& input::placeholder": {
                    color: FOUNDRY_SCREEN_COLORS.TEXT_MUTED,
                    opacity: 1,
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={handlers.search}
                sx={{ 
                  p: 0.75,
                  backgroundColor: search.pendingCodes.length > 0 ? FOUNDRY_SCREEN_COLORS.BACKGROUND_BUTTON_SUCCESS : "transparent",
                  color: search.pendingCodes.length > 0 ? "white" : "inherit",
                  "&:hover": {
                    backgroundColor: search.pendingCodes.length > 0 ? FOUNDRY_SCREEN_COLORS.BACKGROUND_BUTTON_SUCCESS_HOVER : "rgba(0, 0, 0, 0.04)"
                  }
                }}
                title={search.pendingCodes.length > 0 ? `Buscar ${search.pendingCodes.length} códigos` : 'Buscar'}>
                <SearchIcon sx={{ fontSize: 20 }} />
              </IconButton>
              {search.searchCodes.length > 0 && (
                <IconButton
                  size="small"
                  onClick={handlers.clearSearch}
                  sx={{ p: 0.75 }}
                  title='Limpiar búsqueda'
                  color='secondary'>
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              <Typography variant='h6' sx={{ flex: 1, textAlign: "center", fontSize: "0.95rem", fontWeight: 600 }}>
                {translateText("Tabla de MO", lang)}
                {search.searchCodes.length > 0 && (
                  <Typography
                    variant='caption'
                    display='block'
                    sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                    Buscando: {search.searchCodes.join(", ")}
                  </Typography>
                )}
                {search.pendingCodes.length > 0 && (
                  <Typography
                    variant='caption'
                    display='block'
                    sx={{ fontSize: "0.7rem", color: "orange", fontWeight: "bold" }}>
                    Códigos pendientes ({search.pendingCodes.length}): {search.pendingCodes.join(", ")}
                  </Typography>
                )}
              </Typography>
              <IconButton 
                size="small"
                onClick={handlers.toggleResetModal} 
                sx={{ p: 0.75 }}>
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton 
                size="small"
                onClick={handlers.selectAll} 
                sx={{ p: 0.75 }}>
                <CheckIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Button
                variant='contained'
                size="small"
                onClick={actions.crearAgrupacion}
                sx={[buttonStyle, {
                  fontSize: "0.8rem",
                  py: 0.5,
                  px: 1.5,
                }]}>
                {translateText("Crear Agrupación", lang)}
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: "30vh" }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "",
                      "MO",
                      "Aleación",
                      "Referencia",
                      "Cantidad",
                      "Entrega",
                      "Gramos",
                      "Tipo orden",
                      "Acciones",
                    ].map((header, index) => (
                      <TableCell
                        key={index}
                        align='center'
                        sx={{
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          backgroundColor: FOUNDRY_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          py: 1,
                          px: 1.5,
                          fontSize: "0.75rem",
                        }}>
                        {header && translateText(header, lang)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listAleacionOption.map((item, index) => {
                    const isItemSelected = ui.selectedAgrupaciones.some(
                      (selected) => selected.id === item.id
                    );
                    return (
                      <TableRow
                        key={index}
                        selected={isItemSelected}
                        onClick={() => handlers.agrupacionSelect(item)}
                        hover
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_EVEN : FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_ODD,
                          "&.Mui-selected": { backgroundColor: FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_SELECTED },
                          "&:hover": { backgroundColor: `${FOUNDRY_SCREEN_COLORS.BACKGROUND_ROW_HOVER} !important` },
                        }}>
                        <TableCell sx={{ py: 0.75, px: 1.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlers.consultaAsync(item);
                            }}
                            sx={{ p: 0.5 }}>
                            <AddIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{item.mo}</TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{item.aleacion}</TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{item.referencia}</TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{item.cantidad}</TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>
                          {formatDate(item.fechaentrega)}
                        </TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>
                          {(item.gramos * item.cantidad).toFixed(2)}
                        </TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5, fontSize: "0.8rem" }}>{item.tipo_orden}</TableCell>
                        <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              actions.borrarMO(item);
                            }}
                            color='error'
                            sx={{ p: 0.5 }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FoundryScreen;
